import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Avatar,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
} from "@mui/material";
import { useParams } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";

// Import your services and models
import { OrderHistoryService } from "../../services/order/orderHistoryService";
import { CartService } from "../../services/functions/cartService";
import { ImageService } from "../../services/images/images";
import { refundService } from "../../services/firebase/refundService";
import { RefundType, RefundStatus } from "../../models/refundType";
import { CartType, CartItem } from "../../models/CartType";
import { CartServiceRefundDecisionResponseType } from "../../models/CartServiceType";
import AsyncString from "../../shared/asyncString";
import AsyncCardMedia from "../../shared/asyncCardMedia";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useRestaurantOrderHistory } from "../../hooks/useRestaurantOrderHistory";
import { menuService } from "../../services/firebase/menuService";
import { FullMenuPageData } from "../../profilePage/maintainSpecificRestaurant/upsertFullMenuHelpers";
import { getPriceOfMenuCart, MenuCartItem } from "../../models/fullMenuCart";
import { MenuItemDetails } from "../../shared/menuItemDetails";

// Helper: Format currency from cents
export const formatCurrency = (cents: number) =>
  `R${(cents / 100).toFixed(2)}`;

const RefundOrderInStore: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();

  // Order details state
  const [order, setOrder] = useState<CartType | null>(null);
  // Refund request state (if one exists for the order)
  const [refundRequest, setRefundRequest] = useState<RefundType>({
    orderId: orderId ?? "",
    refundAmount: 0,
    refundDecision: "",
    refundImages: [],
    refundMessage: "",
    rufundStatus: "PENDING_RESTAURANT"
  });

  // Refund decision UI state
  const [refundAmount, setRefundAmount] = useState<number>(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [refundMessage, setRefundMessage] = useState("");
  const [isRefundable, setIsRefundable] = useState<boolean>(false);
  const [selectedImage, setSelectedImage] = useState<string>("");
  const [menuData, setMenuData] = useState<FullMenuPageData>({ baseItems: [], menu: { categories: [], bundles: [] } });

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");

  // Load order details and any existing refund request
  const loadData = async () => {
    if (!orderId) return;
    try {
      setLoading(true);
      // Fetch order details (returns a CartType)
      const fetchedOrder = await OrderHistoryService.getOrderById(orderId);
      setOrder(fetchedOrder);

      // Try to fetch an existing refund request for this order.
      // (Assumes refundService.fetchRefundByOrderId is available.)
      if (fetchedOrder) {
        const menu = await menuService.fetchFullMenuCached(fetchedOrder.restaurantId);
        setMenuData(menu);
        setIsRefundable(fetchedOrder.isRefundable);
        if (fetchedOrder.refundRequestId) {
          const fetchedRefund = await refundService.fetchRefundById(fetchedOrder.refundRequestId);
          setRefundRequest(fetchedRefund);
        }
      }
    } catch (err) {
      setError("Failed to load order or refund request.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [orderId]);

  // Handle file selection (for uploading a proof image)
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  // Process the refund decision (full, partial, or deny)
  const handleDecision = async (decision: RefundStatus) => {
    if (!refundRequest || !order) return;
    setLoading(true);
    try {
      if (!isRefundable) {
        setError("Refund already processed.");
        return;
      }
      let imageUrl: string[] = [];
      if (selectedFile) {
        // Upload the image and take the first returned URL
        const urls = await ImageService.uploadImagesToStorage([selectedFile]);
        imageUrl = urls;
      }
      var refundDecision: CartServiceRefundDecisionResponseType | undefined;
      if (decision === "FULL_REFUND_PROCESSING") {
        refundDecision = await CartService.refundDecision({
          orderHistoryId: order.id,
          message: refundMessage == "" ? "REFUND WAS APPROVED WITHOUT MESSAGE" : refundMessage,
          images: imageUrl,
          // Refund Decision,
          refundDecisionMessage: "Auto Approved In Store",
          refundDecisionAmount: order.totalAmount,
          refundDecisionStatus: decision
        });
      } else if (decision === "PARTIAL_REFUND_PROCESSING") {
        refundDecision = await CartService.refundDecision({
          orderHistoryId: order.id,
          message: refundMessage,
          images: imageUrl,
          // Refund Decision,
          refundDecisionMessage: "Auto Approved In Store",
          refundDecisionAmount: refundAmount,
          refundDecisionStatus: decision
        });
      }
      // Refresh the refund request to get the updated status
      await loadData()
    } catch (err) {
      console.error("Error processing refund decision", err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusChipColor = (status: String) => {
    switch (status) {
      case "PAYMENT_COMPLETE":
        return "#A6C48A";
      case "RESTAURANT_ACCEPTED_ORDER":
        return "#A6C48A";
      case "PREPARING_ORDER":
        return "#A6C48A";
      case "READY_FOR_COLLECTION":
        return "#A6C48A";
      case "COLLECTED":
        return "#162C1F";
      case "PARTIAL_REFUND_COMPLETE":
        return "#162C1F";
      case "FULL_REFUND_COMPLETE":
        return "#162C1F";
      case "FULL_REFUND_PROCESSING":
        return "#FFA07A";
      case "PARTIAL_REFUND_PROCESSING":
        return "#FFA07A";
      case "PENDING_RESTAURANT":
        return "#FFA07A";
      case "FAILED":
        return "#FF6F61";
      default:
        return "default";
    }
  };

  const getStatusMessage = (status: String) => {
    console.log(status);
    switch (status) {
      case "PAYMENT_COMPLETE":
        return "Payment Complete";
      case "RESTAURANT_ACCEPTED_ORDER":
        return "Waiting for the restaurant";
      case "PAYMENT_PENDING":
        return "Payment Pending";
      case "PREPARING_ORDER":
        return "Preparing Order";
      case "READY_FOR_COLLECTION":
        return "Ready for Collection";
      case "COLLECTED":
        return "Collected";
      case "PARTIAL_REFUND_COMPLETE":
      case "FULL_REFUND_COMPLETE":
        return "Refund Complete";
      case "REFUND_REJECTED":
        return "Refund Rejected";
      case "FULL_REFUND_PROCESSING":
        return "Full Refund Processing";
      case "PARTIAL_REFUND_PROCESSING":
        return "Partial Refund Processing";
      case "PENDING_RESTAURANT":
        return "Refund Requested";
      case "FAILED":
        return "Failed";
      default:
        return "Unknown Status";
    }
  };


  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 2,
        }}
        className="patlite"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ padding: 3, height: "100%" }} className="patlite">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  const getImgUrl = (item: MenuCartItem) => {
    if (item.bundle) {
      if (item.bundle.imageUrl) {
        return item.bundle.imageUrl
      };
    }
    if (item.comboVariant) {
      if (item.comboVariant.imageUrl) {
        return item.comboVariant.imageUrl;
      }
    }
    const bi = menuData.baseItems.find(bi => bi.id == item.baseItemId);
    if (bi) {
      return bi.imageUrl
    }
    return undefined
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "start",
        alignItems: "center",
        padding: 2,
        height: "100%",
        minHeight: "100vh",
      }}
      className="patlite"
    >

      <Typography variant="h4" textAlign={"center"} gutterBottom>
        Process Refund for Order #{orderId}
      </Typography>

      {/* Order Details Section */}
      {order && (
        <Card sx={{
          p: 3,
          mb: 4,
          justifyContent: "center",
          alignItems: "center",
          alignContent: "center",
          flexDirection: "column",
          height: "100%"
        }}>
          <Typography variant="h6" gutterBottom>
            Order Details
          </Typography>
          <List disablePadding sx={{ alignItems: "center", justifyContent: "center", }}>
            {order.items.map((item: CartItem, index: number) => (
              <ListItem
                key={index}
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  justifyContent: { xs: "flex-start", md: "center" },
                  alignItems: { xs: "flex-start", md: "center" },
                  textAlign: { xs: "left", md: "left" },
                  gap: 2,
                }}
              >
                {getImgUrl(item.menuCartItem) &&
                  <img
                    style={{ height: "80px", width: "80px", borderRadius: 2 }}
                    src={getImgUrl(item.menuCartItem)}
                    alt="Item thumbnail"
                  />
                }


                <MenuItemDetails item={item.menuCartItem} menuData={menuData} />
              </ListItem>
            ))}
          </List>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6">Total Amount:</Typography>
            <Typography variant="h6">{formatCurrency(order.totalAmount)}</Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 2, mt: 3, flexWrap: "wrap" }}>
            <Chip
              label={`Status: ${getStatusMessage(refundRequest.rufundStatus)}`}
              sx={{
                color: "#fff",
                backgroundColor: getStatusChipColor(refundRequest.rufundStatus),
                mt: { xs: 1, sm: 0 },
              }}
            />
            <Chip
              label={`Already Refunded: ${formatCurrency(refundRequest.refundAmount || 0)}`}
              variant="outlined"
            />
          </Box>
          {/* Refund Decision Section */}
          {isRefundable ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                minHeight: "100%",
                width: "100%",
                flexGrow: 1,
              }}
            >
              <Typography variant="h6" gutterBottom>
                Process Refund Decision
              </Typography>
              <TextField
                label="Decision Comments"
                multiline
                fullWidth
                rows={3}
                value={refundMessage}
                onChange={(e) => setRefundMessage(e.target.value)}
                sx={{ mb: 3 }}
              />
              <input
                accept="image/*"
                type="file"
                id="upload-image"
                hidden
                onChange={handleFileChange}
              />
              <label htmlFor="upload-image">
                <Button
                  variant="contained"
                  component="span"
                  startIcon={<CloudUploadIcon />}
                  sx={{
                    mb: 2,
                    borderRadius: "24px",
                    backgroundColor: '#162C1F',
                    '&:hover': {
                      backgroundColor: '#0f1f15',
                    },
                    width: "100%",
                    paddingLeft: 8,
                    paddingRight: 8,
                    minWidth: 300,
                  }}
                >
                  Upload Proof
                </Button>
              </label>

              {selectedFile && (
                <Card sx={{ display: "flex", alignItems: "center", p: 2, mb: 3, gap: 2, }}>
                  <Avatar
                    variant="rounded"
                    src={URL.createObjectURL(selectedFile)}
                    sx={{ width: 60, height: 60 }}
                  />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body2">{selectedFile.name}</Typography>
                  </Box>
                  <IconButton onClick={() => setSelectedFile(null)}>
                    <CloseIcon />
                  </IconButton>
                </Card>
              )}

              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  flexWrap: "wrap",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 2,
                  mt: 3,
                }}
              >
                <Button
                  variant="contained"
                  sx={{ background: "#162C1F", border: "1px solid #162C1F", borderRadius: "24px" }}
                  onClick={() => handleDecision("FULL_REFUND_PROCESSING")}
                >
                  Full Refund ({order ? formatCurrency(order.totalAmount) : ""})
                </Button>

                <Button
                  variant="outlined"
                  sx={{ border: "1px solid #162C1F", borderRadius: "24px" }}
                  onClick={() => handleDecision("PARTIAL_REFUND_PROCESSING")}
                >
                  <Typography variant="button" component="h1" sx={{ color: "#162C1F" }}>
                    Partial Refund
                  </Typography>
                </Button>

                <TextField
                  label="Partial Amount (R)"
                  type="number"
                  value={refundAmount / 100}
                  onChange={(e) => {
                    const valueInCents = Number(e.target.value) * 100;
                    setRefundAmount(
                      order && valueInCents > order.totalAmount
                        ? order.totalAmount
                        : valueInCents
                    );
                  }}
                  InputProps={{
                    inputProps: {
                      min: 0,
                      max: order ? order.totalAmount / 100 : 0,
                    },
                  }}
                  sx={{ width: 200 }}
                />
              </Box>
            </Box>
          ) : (
            <Box sx={{ mt: 2, display: "flex", flexDirection: "column" }}>
              <Typography variant="h6" gutterBottom>
                Refund Decision
              </Typography>
              <TextField
                label="Decision Comments"
                multiline
                fullWidth
                rows={3}
                disabled
                value={refundRequest.refundDecision}
                sx={{ mt: 2 }}
              />
            </Box>
          )}
        </Card>
      )}


      {/* Image Preview Dialog */}
      <Dialog open={!!selectedImage} onClose={() => setSelectedImage("")} maxWidth="lg">
        <DialogTitle>Attachment Preview</DialogTitle>
        <DialogContent>
          {selectedImage && (
            <img
              src={selectedImage}
              alt="Attachment Preview"
              style={{ width: "100%", height: "auto", borderRadius: 8 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedImage("")}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>

  );
};

export default RefundOrderInStore;
